import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_TEXTAREA_CLASS,
} from '../../settings/utils/settingsSectionStyles';

type BusinessProfile = { description: string; max_length: number; can_edit: boolean };

const PLACEHOLDER =
  'z. B. Wir sind ein Leckortungs- und Trocknungsbetrieb mit 12 Mitarbeitern. Wir orten Wasserschäden, trocknen Estrich und Wände technisch und arbeiten meist im Auftrag von Versicherungen und Hausverwaltungen. Heizungs- oder Sanitärinstallation machen wir nicht.';

async function readError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error || fallback;
}

/** „Was macht dein Betrieb?“: fließt in KI-Chat, Mobile-App-Chat, geplante Jobs und E-Mail-Assistent. */
export function BusinessProfilePanel() {
  const [profile, setProfile] = React.useState<BusinessProfile | null>(null);
  const [draft, setDraft] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/ai-business-profile', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, 'Beschreibung konnte nicht geladen werden.'));
        return res.json() as Promise<BusinessProfile>;
      })
      .then((body) => {
        if (cancelled) return;
        setProfile(body);
        setDraft(body.description);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Beschreibung konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/ai-business-profile', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: draft }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Beschreibung konnte nicht gespeichert werden.'));
      const body = (await res.json()) as BusinessProfile;
      setProfile(body);
      setDraft(body.description);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Beschreibung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <InlineLoadingState label="Beschreibung wird geladen …" />;

  const maxLength = profile?.max_length ?? 2000;
  const canEdit = Boolean(profile?.can_edit);
  const dirty = draft.trim() !== (profile?.description ?? '').trim();

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-app-text">Was macht dein Betrieb?</h3>
        <p className="mt-1 text-sm text-app-text-muted">
          Der Assistent nutzt diese Beschreibung im Chat, in der Mobile App, bei geplanten Aufgaben und bei E-Mail-Entwürfen.
          Nenne Gewerke, typische Aufträge und Kunden, und was ihr nicht macht.
        </p>
      </div>
      <label className="block">
        <span className={SETTINGS_FIELD_LABEL}>Beschreibung</span>
        <textarea
          className={`${SETTINGS_TEXTAREA_CLASS} mt-1.5 w-full`}
          rows={8}
          value={draft}
          maxLength={maxLength}
          disabled={!canEdit}
          placeholder={PLACEHOLDER}
          onChange={(event) => setDraft(event.target.value)}
        />
      </label>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-app-text-subtle">
          {draft.length} / {maxLength} Zeichen
        </span>
        {canEdit ? (
          <div className="flex items-center gap-3">
            {saved ? <span className="text-sm text-app-text-muted">Gespeichert</span> : null}
            <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving || !dirty} onClick={() => void save()}>
              Speichern
            </button>
          </div>
        ) : (
          <span className="text-sm text-app-text-muted">Nur Inhaber und Admins können das ändern.</span>
        )}
      </div>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
